import React from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { LabFlag, LabObservationView } from '../../types/labResults';

/**
 * The rendering of lab observations, shared by the clinician chart and the
 * patient portal.
 *
 * One component for both audiences on purpose. The pages around it differ —
 * different copy, different next steps — but the numbers, the units, the
 * reference ranges and the flags are drawn by this file for everyone. The
 * failure mode worth engineering against is a patient and their doctor reading
 * the same result and disagreeing about whether it was flagged, and the surest
 * way to avoid it is to leave no second implementation to drift.
 *
 * Observations are rendered in the order the lab sent them. That ordering is
 * clinically meaningful — related analytes arrive adjacent, and a panel reads
 * as a panel — so sorting abnormal-first would scatter it. The count at the top
 * is what draws the eye to flagged rows instead.
 */

const FLAG_STYLES: Record<LabFlag, { label: string; pill: string; value: string; icon: React.ElementType | null }> = {
  critical_high: {
    label: 'Critically high',
    pill: 'bg-red-100 text-red-800 ring-1 ring-red-300',
    value: 'text-red-700',
    icon: AlertTriangle
  },
  critical_low: {
    label: 'Critically low',
    pill: 'bg-red-100 text-red-800 ring-1 ring-red-300',
    value: 'text-red-700',
    icon: AlertTriangle
  },
  high: {
    label: 'High',
    pill: 'bg-amber-100 text-amber-800',
    value: 'text-amber-700',
    icon: ArrowUp
  },
  low: {
    label: 'Low',
    pill: 'bg-sky-100 text-sky-800',
    value: 'text-sky-700',
    icon: ArrowDown
  },
  abnormal: {
    label: 'Abnormal',
    pill: 'bg-amber-100 text-amber-800',
    value: 'text-amber-700',
    icon: AlertTriangle
  },
  normal: {
    label: 'Normal',
    pill: 'bg-green-50 text-green-700',
    value: 'text-gray-900',
    icon: Minus
  },
  // No badge at all. The lab did not flag this marker, and inventing a "Normal"
  // badge would be us making a clinical statement the lab declined to make.
  unflagged: {
    label: '',
    pill: '',
    value: 'text-gray-900',
    icon: null
  }
};

export function LabFlagBadge({ flag }: { flag: LabFlag }) {
  const style = FLAG_STYLES[flag];
  if (!style.label) return null;
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${style.pill}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {style.label}
    </span>
  );
}

/**
 * A scale showing where the value falls across its reference range.
 *
 * Rendered only when the value is a plain number AND the range parsed into two
 * bounds. A censored value ("<0.3"), a qualitative one ("POSITIVE") and a
 * one-sided or prose range all suppress the bar rather than approximate it —
 * the whole point of the picture is that its position is trustworthy.
 */
function LabRangeBar({ observation }: { observation: LabObservationView }) {
  const { position, rangeLow, rangeHigh, units } = observation;
  if (position === null || rangeLow === null || rangeHigh === null) return null;

  const clamped = Math.max(0, Math.min(1, position));
  const outOfRange = position < 0 || position > 1;

  return (
    <div className="mt-2">
      <div className="relative h-1.5 rounded-full bg-gray-200">
        {/* The in-range band. The whole bar IS the range, so this is the full width. */}
        <div className="absolute inset-0 rounded-full bg-green-200" />
        <div
          className={`absolute top-1/2 h-3 w-3 -translate-y-1/2 -translate-x-1/2 rounded-full ring-2 ring-white ${
            outOfRange ? 'bg-amber-500' : 'bg-green-600'
          }`}
          style={{ left: `${clamped * 100}%` }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-gray-500">
        <span>{rangeLow}</span>
        <span>{rangeHigh}{units ? ` ${units}` : ''}</span>
      </div>
    </div>
  );
}

export function LabObservationRow({ observation }: { observation: LabObservationView }) {
  const style = FLAG_STYLES[observation.flag];

  return (
    <div className={`py-4 ${observation.isAbnormal ? 'bg-amber-50/40 -mx-4 px-4 sm:-mx-6 sm:px-6' : ''}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{observation.name}</span>
            <LabFlagBadge flag={observation.flag} />
          </div>
          {observation.refRange && (
            <p className="mt-0.5 text-xs text-gray-500">
              Reference range {observation.refRange}
              {observation.units ? ` ${observation.units}` : ''}
            </p>
          )}
        </div>
        <div className="text-right">
          <span className={`text-lg font-semibold tabular-nums ${style.value}`}>
            {observation.value || '—'}
          </span>
          {observation.units && (
            <span className="ml-1 text-xs text-gray-500">{observation.units}</span>
          )}
        </div>
      </div>

      <LabRangeBar observation={observation} />

      {observation.notes.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {observation.notes.map((note, i) => (
            <li key={i} className="text-xs text-gray-600">{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface LabResultTableProps {
  observations: LabObservationView[];
  /** Report-level comments from the lab, shown beneath the markers. */
  reportNotes?: string[];
}

export function LabResultTable({ observations, reportNotes = [] }: LabResultTableProps) {
  if (!observations.length) {
    return (
      <p className="py-8 text-center text-sm text-gray-500">
        This report arrived without any individual markers. If the lab sent a PDF it is
        linked above; otherwise contact the clinic.
      </p>
    );
  }

  return (
    <div>
      <div className="divide-y divide-gray-100">
        {observations.map(o => (
          <LabObservationRow key={`${o.code}-${o.name}`} observation={o} />
        ))}
      </div>

      {reportNotes.length > 0 && (
        <div className="mt-6 rounded-lg bg-gray-50 p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Lab comments
          </h4>
          <ul className="mt-2 space-y-1">
            {reportNotes.map((note, i) => (
              <li key={i} className="text-sm text-gray-700">{note}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * The "3 of 10 outside the reference range" line.
 *
 * Phrased as a count, never as an interpretation. "3 markers outside the
 * reference range" is a fact about the report; "3 problems" or "abnormal
 * result" is a clinical conclusion neither this component nor the lab is
 * entitled to draw.
 */
export function LabAbnormalSummary({
  total,
  abnormal,
  className = ''
}: {
  total: number;
  abnormal: number;
  className?: string;
}) {
  if (!total) return null;

  if (!abnormal) {
    return (
      <span className={`inline-flex items-center gap-1.5 text-sm text-green-700 ${className}`}>
        <Minus className="h-4 w-4" />
        All {total} marker{total === 1 ? '' : 's'} within the reference range
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1.5 text-sm text-amber-700 ${className}`}>
      <AlertTriangle className="h-4 w-4" />
      {abnormal} of {total} marker{total === 1 ? '' : 's'} outside the reference range
    </span>
  );
}

/** Status pill for preliminary / final / corrected. */
export function LabStatusBadge({ status, amended }: { status: string; amended?: boolean }) {
  if (amended) {
    return (
      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
        Superseded
      </span>
    );
  }
  if (status === 'preliminary') {
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
        Preliminary
      </span>
    );
  }
  if (status === 'corrected') {
    return (
      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
        Corrected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
      Final
    </span>
  );
}
